import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { DefaultOptions, QueryClient, UseQueryResult } from '@tanstack/react-query';
import { PersistQueryClientProviderProps } from '@tanstack/react-query-persist-client';
import { PaginationSize } from 'components/data-grid/pagination/pagination.utils';
import { DocumentPaginateModel } from 'components/document-paginate-filter';
import { ALLOW_LOCAL_STORAGE } from 'constants/local-flags';
import { ColumnarProvider, Provider } from 'constants/provider';
import { ProviderRegion } from 'constants/provider-region';
import { BucketScopeCollection } from 'hooks/use-document-list-filters/use-document-list-filters';
import { ColumnarDeploymentCostPayload } from 'sync/columnar-configuration-service/columnar-configuration-service.types';
import { DatabaseCostSpecPayload, DefaultDeploymentQueryOptionsV2, DeploymentQueryOptionsV2 } from 'sync/configuration-service';
import { IntegrationTypeParam } from 'sync/integrations-service/integrations-service.types';
import { ModelType } from 'sync/model-service/model-service.types';
import { NameCheckerResourceType } from 'sync/name-checker/name-checker.types';
import { ProxyCollectionContextWithUid } from 'sync/proxy-service';
import { DatabaseContext, PaginationCursorParams, PaginationParams, SortParams, UserContext } from 'sync/request.types';
import { UseMetricDataType } from 'types/metrics';

export const getQueryClient = (defaultOptions: DefaultOptions) =>
  new QueryClient({
    defaultOptions,
  });

export const queryClient = getQueryClient({
  queries: {
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  },
});

export const getMergedQueriesStatus = (queries: Pick<UseQueryResult<unknown, unknown>, 'isLoading' | 'error' | 'isSuccess'>[]) => ({
  isLoading: queries.some(({ isLoading }) => isLoading),
  error: queries.find(({ error }) => error) || null,
  data: {},
  isSuccess: queries.every(({ isSuccess }) => isSuccess),
});

export const ReactQueryKeys = {
  SUPPORT_SERVICE_LIST: (organizationId: string, page: PaginationParams, sortBy?: string) => [
    'supportServiceList',
    organizationId,
    page,
    sortBy,
  ],
  SUPPORT_TICKET: (ticketId: string) => ['supportTicket', ticketId],
  SUPPORT_COMMENTS: (organizationId: string, id: string, page?: PaginationCursorParams, sort?: string) => [
    'supportComments',
    organizationId,
    id,
    page,
    sort,
  ],
  GET_BUCKET_BACKUPS_LIST: (organizationId: string, projectId: string, databaseId: string, bucketId?: string) => [
    'bucketBackupsList',
    organizationId,
    projectId,
    databaseId,
    bucketId,
  ],

  DATABASE_ALLOWED_IP_LIST: ({ databaseId, organizationId, projectId }: DatabaseContext) => [
    'databaseAllowedIpAddressList',
    databaseId,
    organizationId,
    projectId,
  ],
  APP_SERVICE_PROJECT_DATABASES: (organizationId: string) => ['appServiceProjectDatabases', organizationId],
  APP_SERVICE_DEPLOYMENT_COSTS: (
    organizationId: string,
    config: {
      compute: string;
      provider: string;
      region: string;
      scale: number;
    }
  ) => ['appServiceDeploymentCosts', organizationId, config],
  APP_SERVICE_DEPLOYMENT_OPTIONS: (organizationId: string) => ['appServiceDeploymentOptions', organizationId],
  APP_SERVICE_SPECS: (appServiceId: string, projectId: string, organizationId: string, databaseId: string) => [
    'appServiceSpecs',
    appServiceId,
    projectId,
    organizationId,
    databaseId,
  ],
  DEPLOYMENT_COSTS: (organizationId: string) => ['deploymentCosts', organizationId],
  ORGANIZATION_API_KEY_LIST: (organizationId: string, page: PaginationParams) => ['organizationApiKeyList', organizationId, page],
  ORGANIZATION_API_KEY_DETAILS: (organizationId: string, keyId: string) => ['organizationApiKeyDetails', organizationId, keyId],
  ORGANIZATION: (organizationId: string) => ['organization', organizationId],
  ORGANIZATION_LIST: () => ['organizationList'],
  TRIAL: (organizationId: string) => ['trial', organizationId],
  TRIAL_DATABASE: (organizationId: string) => ['trialDatabase', organizationId],
  PROJECT_LIST: ({
    organizationId,
    page,
    perPage,
    entire,
    sortBy,
    sortDirection,
  }: {
    organizationId: string;
    page: number;
    perPage: number;
    entire: boolean;
    sortBy?: string;
    sortDirection?: string;
  }) => ['projectList', organizationId, page, perPage, entire, sortBy, sortDirection],
  PROJECT: (organizationId: string, projectId: string) => ['project', organizationId, projectId],
  LIBRARY: (databaseId: string, library?: string, bucket?: string, scope?: string) => ['library', databaseId, library, bucket, scope],
  PROJECT_API_KEY_LIST: (organizationId: string, projectId: string, page: PaginationParams) => [
    'projectApiKeyList',
    organizationId,
    projectId,
    page,
  ],
  PROJECT_API_KEY_DETAILS: (organizationId: string, projectId: string, keyId: string) => [
    'projectApiKeyDetails',
    organizationId,
    projectId,
    keyId,
  ],
  DATABASE_LIST: (organizationId: string, page: number, perPage: number, entire: boolean, sortBy?: string, sortDirection?: string) => [
    'database',
    organizationId,
    page,
    perPage,
    entire,
    sortBy,
    sortDirection,
  ],
  ACCESSIBLE_DATABASES: (organizationId: string) => ['accessibleDatabases', organizationId],
  DATABASE_NODE_LIST: (organizationId: string, projectId: string, databaseId: string) => [
    'databaseNodeList',
    organizationId,
    projectId,
    databaseId,
  ],
  DATABASE: (organizationId: string, projectId: string, databaseId: string) => ['database', organizationId, projectId, databaseId],
  DATABASE_JOBS: (organizationId: string, projectId: string, databaseId: string) => ['databaseJobs', organizationId, projectId, databaseId],
  DATABASE_COSTS: ({ organizationId, projectId, databaseId }: { organizationId: string; projectId: string; databaseId: string }) => [
    'databaseCosts',
    organizationId,
    projectId,
    databaseId,
  ],
  DATABASE_ACCESS_LIST: (organizationId: string, databaseId: string, projectId: string, page: PaginationParams, sort?: SortParams) => [
    'databaseAccessList',
    projectId,
    organizationId,
    databaseId,
    page,
    sort,
  ],
  VALID_BUCKETS_AI_LIST: (organizationId: string, databaseId: string, projectId: string, catalogType: string) => [
    'validBucketsAIList',
    projectId,
    organizationId,
    databaseId,
    catalogType,
  ],
  DATABASE_ACCESS_ITEM: (organizationId: string, projectId: string, databaseId: string, keyId: string) => [
    'databaseAccessItem',
    organizationId,
    projectId,
    databaseId,
    keyId,
  ],
  DATABASE_SCHEDULE_LIST: (organizationId: string, databaseId: string, projectId: string) => [
    'databaseScheduleList',
    organizationId,
    projectId,
    databaseId,
  ],
  DATABASE_SNAPSHOTS: (
    organizationId: string,
    projectId: string,
    databaseId: string,
    page: number,
    perPage: number,
    sortBy?: string,
    sortDirection?: string,
    filterDate?: {
      startDate: string;
      endDate: string;
    }
  ) => ['databaseSnapshots', organizationId, projectId, databaseId, page, perPage, sortBy, sortDirection, filterDate],
  DATABASE_SNAPSHOTS_SCHEDULE: (organizationId: string, projectId: string, databaseId: string) => [
    'databaseSnapshotsSchedule',
    organizationId,
    projectId,
    databaseId,
  ],
  DATABASE_SNAPSHOTS_RESTORES: (
    organizationId: string,
    projectId: string,
    databaseId: string,
    page: number,
    perPage: number,
    sortBy?: string,
    sortDirection?: string
  ) => ['databaseSnapshotsRestores', organizationId, projectId, databaseId, page, perPage, sortBy, sortDirection],
  DATABASE_SNAPSHOTS_COST: (organizationId: string, projectId: string, databaseId: string) => [
    'databaseSnapshotsCost',
    organizationId,
    projectId,
    databaseId,
  ],
  DATABASE_METADATA: (databaseId: string) => ['databaseMetadata', databaseId],
  FUNCTION_METADATA: (databaseId: string) => ['functionMetadata', databaseId],
  DATABASE_REPLICATIONS_SELF_MANAGED_LIST: (
    organizationId: string,
    projectId: string,
    databaseId: string,
    page: number,
    perPage: number,
    sortBy?: string,
    sortDirection?: string
  ) => ['databaseReplicationSelfManagedList', organizationId, projectId, databaseId, page, perPage, sortBy, sortDirection],
  DATABASE_REPLICATION: (organizationId: string, projectId: string, databaseId: string, replicationId: string) => [
    'databaseReplication',
    organizationId,
    projectId,
    databaseId,
    replicationId,
  ],
  DATABASE_REPLICATION_ADVANCED_SETTINGS: (organizationId: string, projectId: string, databaseId: string, replicationId: string) => [
    'databaseReplicationAdvancedSettings',
    organizationId,
    projectId,
    databaseId,
    replicationId,
  ],
  DATABASE_REPLICATIONS_LIST: (
    organizationId: string,
    projectId: string,
    databaseId: string,
    page: number,
    perPage: number,
    sortBy?: string,
    sortDirection?: string
  ) => ['databaseReplicationList', organizationId, projectId, databaseId, page, perPage, sortBy, sortDirection],
  APP_SERVICE: (appServiceId: string, projectId: string, organizationId: string, databaseId: string) => [
    'appService',
    appServiceId,
    projectId,
    organizationId,
    databaseId,
  ],
  WORKFLOW_GET_SERVICE: (workflowId: string, projectId: string, organizationId: string, databaseId: string) => [
    'workflowGetService',
    workflowId,
    projectId,
    organizationId,
    databaseId,
  ],
  AI_LIBRARY_INDIVIDUAL_GET: (
    catalogType: string,
    catalogTypeIdentifier: string,
    projectId: string,
    organizationId: string,
    databaseId: string
  ) => ['aiLibraryIndividualGetService', catalogType, catalogTypeIdentifier, projectId, organizationId, databaseId],
  APP_SERVICES_LIST: (organizationId: string, page: number, perPage: number, entire: boolean, sortBy?: string, sortDirection?: string) => [
    'appServicesList',
    organizationId,
    page,
    perPage,
    entire,
    sortBy,
    sortDirection,
  ],
  WORKFLOWS_LIST: (organizationId: string, page: number, perPage: number, entire: boolean, sortBy?: string, sortDirection?: string) => [
    'workflowsList',
    organizationId,
    page,
    perPage,
    entire,
    sortBy,
    sortDirection,
  ],
  APP_ENDPOINT: (organizationId: string, projectId: string, databaseId: string, appServiceId: string, appEndpointId: string) => [
    'appEndpoint',
    organizationId,
    projectId,
    databaseId,
    appServiceId,
    appEndpointId,
  ],
  APP_ROLE_LIST: (organizationId: string, projectId: string, databaseId: string, appServiceId: string, appEndpointId: string) => [
    'appRoleList',
    organizationId,
    projectId,
    databaseId,
    appServiceId,
    appEndpointId,
  ],
  APP_ROLE_GET: (
    organizationId: string,
    projectId: string,
    databaseId: string,
    appServiceId: string,
    appEndpointId: string,
    appRoleId: string
  ) => ['appRoleGet', organizationId, projectId, databaseId, appServiceId, appEndpointId, appRoleId],

  APP_ENDPOINT_ACCESS_VALIDATION_LIST: (
    organizationId: string,
    projectId: string,
    databaseId: string,
    appServiceId: string,
    appEndpointId: string
  ) => ['appEndpointAccessValidationList', organizationId, projectId, databaseId, appServiceId, appEndpointId],
  APP_ENDPOINT_COLLECTION_DOC: (
    organizationId: string,
    projectId: string,
    databaseId: string,
    appServiceId: string,
    appEndpointId: string,
    scopeKey: string,
    collectionKey: string
  ) => ['appEndpointCollectionDoc', organizationId, projectId, databaseId, appServiceId, appEndpointId, scopeKey, collectionKey],
  APP_ENDPOINT_LIST: (
    organizationId: string,
    projectId: string,
    databaseId: string,
    appServiceId: string,
    page: PaginationParams,
    sortBy?: string,
    sortDirection?: string
  ) => ['appEndpointList', organizationId, projectId, databaseId, appServiceId, page, sortBy, sortDirection],
  APP_ENDPOINT_USERS_LIST: (
    appEndpointId: string,
    appServiceId: string,
    organizationId: string,
    databaseId: string,
    projectId: string,
    page: PaginationParams,
    sort?: SortParams
  ) => ['appEndpointUsersList', projectId, organizationId, databaseId, appServiceId, appEndpointId, page, sort],
  APP_ENDPOINT_USER: (
    appEndpointId: string,
    appServiceId: string,
    organizationId: string,
    databaseId: string,
    projectId: string,
    name: string
  ) => ['appEndpointUser', projectId, organizationId, databaseId, appServiceId, appEndpointId, name],
  COLLECTION_APP_ENDPOINT_MAP: (
    organizationId: string,
    projectId: string,
    databaseId: string,
    appServiceId: string,
    scopeName: string,
    bucketName: string
  ) => ['collectionAppEndpointMap', organizationId, projectId, databaseId, appServiceId, scopeName, bucketName],

  CREATE_APP_ENDPOINT_BUCKET_OPTIONS_SERVICE: (organizationId: string, projectId: string, databaseId: string, appServiceId: string) => [
    'createAppEndpointBucketOptionsService',
    organizationId,
    projectId,
    databaseId,
    appServiceId,
  ],
  CREATE_APP_ENDPOINT_SCOPE_OPTIONS_SERVICE: (bucketId: string, databaseId: string) => [
    'createAppEndpointScopeOptionsService',
    bucketId,
    databaseId,
  ],
  CREATE_APP_ENDPOINT_SERVICE: (organizationId: string, projectId: string, databaseId: string, appServiceId: string) => [
    'createAppEndpointService',
    organizationId,
    projectId,
    databaseId,
    appServiceId,
  ],
  INVITATIONS: () => ['invitations'],
  APP_ENDPOINT_SYNC_STATUS: () => ['appEndpointSyncStatus'],
  EVENT_LIST: ({
    organizationId,
    page,
    perPage,
    sortBy,
    sortDirection,
    actor,
    databaseId,
    projectId,
    severity,
    tag,
    start,
    end,
  }: {
    sortBy: string;
    sortDirection: 'asc' | 'desc';
    organizationId: string;
    page: number;
    perPage: number;
    actor?: string;
    databaseId?: string;
    projectId?: string;
    severity?: string;
    tag?: string;
    start?: string;
    end?: string;
  }) => ['eventList', organizationId, page, perPage, actor, projectId, databaseId, severity, tag, start, end, sortBy, sortDirection],
  EVENT: (organizationId: string, eventId: string) => ['organizationEvent', organizationId, eventId],
  ORGANIZATION_USER_LIST: ({
    organizationId,
    page,
    perPage,
    sortBy,
    sortDirection,
  }: {
    organizationId: string;
    page: number;
    perPage: number;
    sortBy?: string;
    sortDirection?: string;
  }) => ['organizationUserList', organizationId, page, perPage, sortBy, sortDirection],
  ORGANIZATION_ALL_USERS_LIST: ({
    organizationId,
    sortBy,
    sortDirection,
  }: {
    organizationId: string;
    sortBy?: string;
    sortDirection?: string;
  }) => ['organizationAllUsersList', organizationId, sortBy, sortDirection],
  SINGLE_DOCUMENT: ({
    bucket,
    collection,
    scope,
    databaseId,
    documentId,
  }: {
    bucket: string;
    collection: string;
    scope: string;
    databaseId: string;
    documentId: string;
  }) => ['singleDocument', bucket, collection, scope, databaseId, documentId],
  ORGANIZATION_USER: ({ organizationId, userId }: { organizationId: string; userId: string }) => [
    'organizationUser',
    organizationId,
    userId,
  ],
  TEAM: ({ organizationId, teamId }: { organizationId: string; teamId: string }) => ['team', organizationId, teamId],
  TEAM_LIST: ({
    organizationId,
    page,
    perPage,
    sortBy,
    sortDirection,
  }: {
    organizationId: string;
    page: number;
    perPage: number;
    sortBy?: string;
    sortDirection?: string;
  }) => ['team', organizationId, page, perPage, sortBy, sortDirection],
  MEMBERS_LIST: ({
    organizationId,
    teamId,
    page,
    perPage,
    sortBy,
    sortDirection,
  }: {
    organizationId: string;
    teamId: string;
    page: number;
    perPage: number;
    sortBy?: string;
    sortDirection?: string;
  }) => ['members', organizationId, teamId, page, perPage, sortBy, sortDirection],
  DATABASE_BUCKETS: (databaseId: string) => ['databaseBuckets', databaseId],
  PROXY_SCOPE_LIST: (databaseId: string, bucketId: string, key?: string) => ['proxyScopeList', databaseId, bucketId, key],
  PROXY_INDEXES: (databaseId: string) => ['proxyIndexes', databaseId],
  PROXY_GENERAL_STATS: (databaseId: string, bucketId: string) => ['generalIndexStats', databaseId, bucketId],
  PROXY_INDEXES_STATS: (databaseId: string) => ['indexesStats', databaseId],
  DATABASE_PROJECT: (organizationId: string, projectId: string, page: number, perPage: number, sortBy: string, sortDirection: string) => [
    'databaseProject',
    organizationId,
    projectId,
    page,
    perPage,
    sortBy,
    sortDirection,
  ],

  PROJECT_INTEGRATION: (organizationId: string, projectId: string, integrationId: string) => [
    'projectIntegration',
    organizationId,
    projectId,
    integrationId,
  ],
  PROJECT_INTEGRATION_LIST: (
    organizationId: string,
    projectId: string,
    page: number,
    perPage: PaginationSize,
    sortBy?: string,
    sortDirection?: string
  ) => ['projectIntegrationList', organizationId, projectId, page, perPage, sortBy, sortDirection],
  PROJECT_USER_LIST: (
    organizationId: string,
    projectId: string,
    page: number,
    perPage: PaginationSize,
    sortBy?: string,
    sortDirection?: string
  ) => ['projectUserList', organizationId, projectId, page, perPage, sortBy, sortDirection],

  DELETE_PROJECT_INTEGRATION: (organizationId: string, projectId: string) => ['deleteProjectIntegration', organizationId, projectId],
  REALM_LIST: (organizationId: string, paginationDetails: PaginationParams, sortDetails: SortParams) => [
    'realmList',
    organizationId,
    paginationDetails,
    sortDetails,
  ],
  AVAILABLE_PROJECT_INVITEES: (organizationId: string, projectId: string) => ['availableProjectInvitees', organizationId, projectId],
  DATABASE_DEPLOYMENT_OPTIONS_SERVICE: (organizationId: string, provider: string, region?: string) => [
    'databaseDeploymentOptionsService',
    organizationId,
    provider,
    region,
  ],
  DATABASE_PROVIDER_OPTIONS_SERVICE: (organizationId: string, provider: string, region?: string) => [
    'databaseProviderOptionsService',
    organizationId,
    provider,
    region,
  ],
  DATABASE_CREATE_COST: (organizationId: string, specs: DatabaseCostSpecPayload[], provider: Provider, region: ProviderRegion) => [
    'databaseCreateCost',
    organizationId,
    specs,
    provider,
    region,
  ],
  DATABASE_CONFIGURATION: (
    organizationId: string,
    projectId: string,
    databaseId: string,
    page: number,
    perPage: PaginationSize,
    sortBy?: string,
    sortDirection?: 'asc' | 'desc',
    date?: string
  ) => ['databaseConfiguration', organizationId, projectId, databaseId, page, perPage, sortBy, sortDirection, date],
  DATABASE_DEPLOYMENT_TYPES_V2: (organizationId: string) => ['databaseDeploymentTypesV2', organizationId],
  DATABASE_DEFAULT_DEPLOYMENT_TYPES_V2: (organizationId: string, { filter }: DefaultDeploymentQueryOptionsV2) => [
    'databaseDefaultDeploymentTypesV2',
    organizationId,
    filter,
  ],
  DATABASE_DEPLOYMENT_REGIONS_V2: (organizationId: string, { option, provider }: DeploymentQueryOptionsV2) => [
    'databaseDeploymentRegionsV2',
    organizationId,
    option,
    provider,
  ],
  DATABASE_DEPLOYMENT_SERVICE_GROUPS_V2: (organizationId: string, { option, provider, region }: DeploymentQueryOptionsV2) => [
    'databaseDeploymentServiceGroupsV2',
    organizationId,
    option,
    provider,
    region,
  ],
  BUCKET_LIST: (organizationId: string, projectId: string, databaseId: string) => ['bucketList', organizationId, projectId, databaseId],
  CONNECT_ENDPOINT: (organizationId: string, projectId: string, databaseId: string, appServiceId: string, appEndpointId: string) => [
    'connectEndpoint',
    organizationId,
    projectId,
    databaseId,
    appServiceId,
    appEndpointId,
  ],
  MAINTAINENCE_PREFERENCE: (organizationId: string, projectId: string, databaseId: string, appServiceId: string) => [
    'maintainencePreference',
    organizationId,
    projectId,
    databaseId,
    appServiceId,
  ],
  CLUSTER_MAINTENANCE_PREFERENCE: (organizationId: string, projectId: string, databaseId: string) => [
    'clusterMaintainencePreference',
    organizationId,
    projectId,
    databaseId,
  ],
  CERTIFICATE_ENDPOINT: (organizationId: string, projectId: string, databaseId: string, appServiceId: string, appEndpointId: string) => [
    'certificateEndpoint',
    organizationId,
    projectId,
    databaseId,
    appServiceId,
    appEndpointId,
  ],
  APP_SERVICE_ALLOWED_IP_LIST: (organizationId: string, projectId: string, databaseId: string, appServiceId: string) => [
    'appServiceAllowedIpList',
    organizationId,
    projectId,
    databaseId,
    appServiceId,
  ],
  ALLOWED_IP_SERVICE_MY_IP: (organizationId: string, projectId: string, databaseId: string) => [
    'allowedIpServiceMyIp',
    organizationId,
    projectId,
    databaseId,
  ],
  MY_IP: (organizationId: string) => ['myIp', organizationId],
  DATABASE_CERTIFICATE: (databaseId: string) => ['databaseCertificate', databaseId],
  SERVERLESS_DATABASE_KEYS: (organizationId: string, projectId: string, databaseId: string) => [
    'serverlessDatabaseKeys',
    organizationId,
    projectId,
    databaseId,
  ],
  COLLECTION_COUNT: (databaseId: string, collections: ProxyCollectionContextWithUid[]) => ['collectionCount', databaseId, collections],
  COLLECTION_SCHEMAS: (databaseId: string, scopeId: string, bucketId: string, collectionId: string) => [
    'collectionSchemas',
    databaseId,
    scopeId,
    bucketId,
    collectionId,
  ],
  DOCUMENTS_PROXY_SERVICE: (documentFilterProps: BucketScopeCollection & DocumentPaginateModel) => [
    'documentsProxyService',
    documentFilterProps,
  ],
  INSTANCE_SCHEDULE_LIST: (organizationId: string, databaseId: string, projectId: string) => [
    'instanceScheduleList',
    organizationId,
    projectId,
    databaseId,
  ],
  INSTANCE_CERTIFICATES: (organizationId: string, projectId: string, databaseId: string) => [
    'instanceCertificates',
    organizationId,
    projectId,
    databaseId,
  ],
  ALLOWED_IP_SERVICE_DATABASE_WITH_ABORT: (organizationId: string, databaseId: string, projectId: string) => [
    'allowedIpServiceDatabaseWithAbort',
    organizationId,
    databaseId,
    projectId,
  ],
  REPLICATION_SELF_MANAGED_INIT_PRE_CHECK: (organizationId: string, databaseId: string, projectId: string, hostname: string) => [
    'replicationSelfManagedInitPreCheck',
    organizationId,
    databaseId,
    projectId,
    hostname,
  ],
  REPLICATION_SELF_MANAGED_PRE_CHECK: (
    organizationId: string,
    databaseId: string,
    projectId: string,
    taskId: string,
    srcSeedHostname: string
  ) => ['replicationSelfManagedPreCheck', organizationId, databaseId, projectId, taskId, srcSeedHostname],
  COLUMNAR_COSTS: ({ organizationId, projectId, databaseId }: { organizationId: string; projectId: string; databaseId: string }) => [
    'columnarCosts',
    organizationId,
    projectId,
    databaseId,
  ],

  COLUMNAR_DEPLOYMENT_OPTIONS: (organizationId: string, provider: ColumnarProvider | null, region: string | null) => [
    'columnarDeploymentOptions',
    organizationId,
    {
      provider,
      region,
    },
  ],
  COLUMNAR_DEPLOYMENT_COSTS: (
    organizationId: string,
    provider: ColumnarProvider,
    region: string,
    specs: ColumnarDeploymentCostPayload['specs']
  ) => ['columnarDeploymentCosts', organizationId, provider, region, specs],
  TOOLS_PROMPTS_LIST: (
    organizationId: string,
    catalogType: 'tools' | 'prompt',
    paginationDetails: PaginationParams,
    clusterId: string,
    bucketsLength: number,
    description: string,
    annotationKey: string,
    annotationValue: string,
    itemName: string,
    recordKind: string[]
  ) => [
    'toolsPromptsList',
    organizationId,
    catalogType,
    paginationDetails,
    clusterId,
    bucketsLength,
    description,
    annotationKey,
    annotationValue,
    itemName,
    recordKind,
  ],

  COLUMNAR_SNAPSHOTS_COST: (organizationId: string, projectId: string, databaseId: string) => [
    'getInstanceSnapshotCost',
    organizationId,
    projectId,
    databaseId,
  ],
  COLUMNAR_SNAPSHOTS_SCHEDULE: (organizationId: string, projectId: string, databaseId: string) => [
    'getInstanceSnapshotSchedule',
    organizationId,
    projectId,
    databaseId,
  ],
  COLUMNAR_SNAPSHOTS: (
    organizationId: string,
    projectId: string,
    databaseId: string,
    page: number,
    perPage: number,
    sortBy?: string,
    sortDirection?: string
  ) => ['listInstanceSnapshots', organizationId, projectId, databaseId, page, perPage, sortBy, sortDirection],

  COLUMNAR_SNAPSHOTS_RESTORES: (
    organizationId: string,
    projectId: string,
    databaseId: string,
    page: number,
    perPage: number,
    sortBy?: string,
    sortDirection?: string
  ) => ['columnarSnapshotsRestores', organizationId, projectId, databaseId, page, perPage, sortBy, sortDirection],
  COLUMNAR_SCHEDULE_CONFIG: (organizationId: string, projectId: string, instanceId: string) => [
    'columnarScheduleConfig',
    organizationId,
    projectId,
    instanceId,
  ],
  GET_MAINTENANCE_JOB: (maintenanceId: string, appServiceId: string, organizationId: string, databaseId: string, projectId: string) => [
    'getMaintenanceJob',
    maintenanceId,
    appServiceId,
    databaseId,
    organizationId,
    projectId,
  ],
  COLUMNAR_MAINTENANCE_PREFERENCE: (organizationId: string, projectId: string, databaseId: string) => [
    'columnarMaintenancePreference',
    organizationId,
    projectId,
    databaseId,
  ],
  GET_COLUMNAR_MAINTENANCE_JOB: (maintenanceId: string, organizationId: string, databaseId: string, projectId: string) => [
    'getMaintenanceJob',
    maintenanceId,
    organizationId,
    databaseId,
    projectId,
  ],
  COLUMNAR_MAINTENANCE_JOB_LIST: (
    organizationId: string,
    projectId: string,
    databaseId: string,
    paginationDetails: PaginationParams,
    sortBy?: string,
    sortDirection?: string
  ) => ['columnarMaintenanceJobList', organizationId, projectId, databaseId, paginationDetails, sortBy, sortDirection],
  APP_ADMIN_LIST_USERS: (
    organizationId: string,
    projectId: string,
    databaseId: string,
    appServiceId: string,
    paginationDetails: PaginationParams,
    sortDetails: SortParams
  ) => ['appAdminListUsers', organizationId, projectId, databaseId, appServiceId, paginationDetails, sortDetails],

  APP_ADMIN_LIST_ENDPOINT_USERS: (
    organizationId: string,
    projectId: string,
    databaseId: string,
    appServiceId: string,
    appEndpointId: string,
    paginationDetails: PaginationParams,
    sortDetails: SortParams
  ) => ['appAdminListUsers', organizationId, projectId, databaseId, appServiceId, appEndpointId, paginationDetails, sortDetails],
  APP_ADMIN_GET_USER: (organizationId: string, projectId: string, databaseId: string, appServiceId: string, userId: string) => [
    'appAdminGetUser',
    organizationId,
    projectId,
    databaseId,
    appServiceId,
    userId,
  ],

  MAINTENANCE_JOB_LIST: (
    organizationId: string,
    projectId: string,
    databaseId: string,
    appServiceId: string,
    page: PaginationParams,
    sortBy?: string,
    sortDirection?: string
  ) => ['maintenanceJobList', appServiceId, organizationId, projectId, databaseId, page, sortBy, sortDirection],
  CLUSER_MAINTENANCE_JOB_LIST: (
    organizationId: string,
    projectId: string,
    databaseId: string,
    page: PaginationParams,
    sortBy?: string,
    sortDirection?: string
  ) => ['clusterMaintenanceJobList', organizationId, projectId, databaseId, page, sortBy, sortDirection],
  USER: (userContext: UserContext) => ['user', userContext],
  INDEXES: (databaseId: string) => ['index', databaseId],
  INDEX_BY_NAME: (databaseId: string, indexName: string) => ['index', databaseId, indexName],
  AI_SERVICE: (organizationId: string) => ['aiService', organizationId],
  ANALYSER: (databaseId: string) => ['analyser', databaseId],
  DATETIMEPARSER: (databaseId: string) => ['datetimeParser', databaseId],
  DATETIMEPARSERNAMES: (databaseId: string) => ['datetimeParserNames', databaseId],
  TOKENIZERNAMES: (databaseId: string) => ['tokenizerNames', databaseId],
  TOKENIZERFILTERNAMES: (databaseId: string) => ['tokenizerFilterNames', databaseId],
  CHARFILTERNAMES: (databaseId: string) => ['charFilterNames', databaseId],
  TOKENMAPNAMES: (databaseId: string) => ['tokenMapNames', databaseId],
  SSO_PRESENCE: (email: string) => ['ssoPresence', email],
  BILLING_ALERTS: (organizationId: string) => ['billingAlerts', organizationId],
  BILLING_ALERT: (organizationId: string, alertId: string) => ['billingAlert', organizationId, alertId],
  BILLING_AVAILABLE_ALERTS: (organizationId: string) => ['billingAvailableAlerts', organizationId],
  BILLING_PLANS: (organizationId: string) => ['billingPlans', organizationId],
  BILLING_FREQUENCIES: (organizationId: string) => ['billingFrequencies', organizationId],
  BILLING_CATEGORIES: (organizationId: string) => ['billingCategories', organizationId],
  BILLING_LAST_TWO_MONTHS: (organizationId: string) => ['billingLastTwoMonths', organizationId],
  BILLING_AVAILABLE_CREDITS: (organizationId: string) => ['billingAvailableCredits', organizationId],
  BILLING_PAY_AS_YOU_GO_USAGE: (organizationId: string) => ['billingPayAsYouGoUsage', organizationId],
  BILLING_STATS: (
    organizationId: string,
    start: string,
    end: string,
    filters?: {
      plans?: string[];
      categories?: string[];
      projectIds?: string[];
      instanceIds?: string[];
    }
  ) => ['billingStats', organizationId, start, end, filters],
  BILLING_INSTANCES: (organizationId: string, start: string, end: string) => ['billingInstances', organizationId, start, end],
  GLOBAL_SEARCH: (organizationId: string, term: string) => ['globalSearch', organizationId, term],
  INSTANCE: (organizationId: string, projectId: string, databaseId: string) => ['instance', organizationId, projectId, databaseId],
  METRIC_DATA: ({ organizationId, projectId, databaseId, metricName, pollInterval, isColumnar, ...rangeOptions }: UseMetricDataType) => [
    'metricData',
    organizationId,
    projectId,
    databaseId,
    metricName,
    rangeOptions,
    isColumnar,
    pollInterval,
  ],
  INSTANCE_LIST: (organizationId: string, page: number, perPage: number, sortBy?: string, sortDirection?: string) => [
    'instanceList',
    organizationId,
    page,
    perPage,
    sortBy,
    sortDirection,
  ],
  ALL_INSTANCE_LIST: (organizationId: string, sortBy?: string, sortDirection?: string) => [
    'allInstanceList',
    organizationId,
    sortBy,
    sortDirection,
  ],

  INSTANCE_PROJECT_LIST: (organizationId: string, page: number, perPage: number, sortBy?: string, sortDirection?: string) => [
    'instanceProjectList',
    organizationId,
    page,
    perPage,
    sortBy,
    sortDirection,
  ],

  INSTANCE_ACCOUNTS: (
    organizationId: string,
    projectId: string,
    databaseId: string,
    paginationDetails: PaginationParams,
    sortDetails: SortParams
  ) => ['instanceAccounts', organizationId, projectId, databaseId, paginationDetails, sortDetails],
  INSTANCE_ACCOUNT: (organizationId: string, projectId: string, databaseId: string, accountId: string) => [
    'instanceAccounts',
    organizationId,
    projectId,
    databaseId,
    accountId,
  ],
  INSTANCE_ROLE: (organizationId: string, projectId: string, databaseId: string, roleId: string) => [
    'instanceRoles',
    organizationId,
    projectId,
    databaseId,
    roleId,
  ],
  INSTANCE_ROLES: (
    organizationId: string,
    projectId: string,
    databaseId: string,
    paginationDetails: PaginationParams,
    sortDetails: SortParams
  ) => ['instanceRoles', organizationId, projectId, databaseId, paginationDetails, sortDetails],
  INSTANCE_METADATA: (databaseId: string) => ['instanceMetadata', databaseId],
  GET_SSO_USERS_LIST: (realmName: string, page: number, perPage: number, sortBy: string, sortDirection: string) => [
    'getSsoUsersList',
    realmName,
    page,
    perPage,
    sortBy,
    sortDirection,
  ],
  LOGIN_FLAGS_LIST: (organizationId?: string) => ['loginFlagsList', organizationId],
  GET_REALM_DETAILS: (organizationId: string, realmId: string) => ['getRealmDetails', organizationId, realmId],
  GET_REALM_NAME: (organizationId: string) => ['getRealmName', organizationId],
  SUPPORT_ENTITY_LIST: (topic: string, organizationId: string, projectId: string) => [
    'supportEntityList',
    topic,
    organizationId,
    projectId,
  ],
  SUPPORT_CLUSTER_LIST: (organizationId: string) => ['supportClusterList', organizationId],
  HEALTH_REPORT_LIST: (organizationId: string, projectId: string, databaseId: string) => [
    'healthReportList',
    organizationId,
    projectId,
    databaseId,
  ],
  HEALTH_ADVICE: (
    organizationId: string,
    projectId: string,
    databaseId: string,
    reportId: string,
    page: number,
    perPage: number,
    category: string,
    severity?: string,
    sortBy?: string,
    sortDirection?: string
  ) => ['healthAdvice', organizationId, projectId, databaseId, reportId, page, perPage, category, severity, sortBy, sortDirection],
  HEALTH_STATS_CATEGORIES: (organizationId: string, projectId: string, databaseId: string, reportId: string) => [
    'healthStatsCategories',
    organizationId,
    projectId,
    databaseId,
    reportId,
  ],
  HEALTH_REPORT_GENERATION_PROGRESS: (organizationId: string, projectId: string, databaseId: string) => [
    'healthReportGenerationProgress',
    organizationId,
    projectId,
    databaseId,
  ],
  IQ_INSIGHTS: (organizationId: string, result: string) => ['iQInsights', organizationId, result],
  GET_HEALTH_ADVISOR_SETTINGS: (organizationId: string, projectId: string, databaseId: string) => [
    'getHealthAdvisorSettings',
    organizationId,
    projectId,
    databaseId,
  ],
  GET_PRIVATE_ENDPOINT_STATUS: (organizationId: string, projectId: string, databaseId: string) => [
    'getPrivateEndpointStatus',
    organizationId,
    projectId,
    databaseId,
  ],
  GET_PRIVATE_ENDPOINT_DETAILS: (organizationId: string, projectId: string, databaseId: string) => [
    'getPrivateEndpointDetails',
    organizationId,
    projectId,
    databaseId,
  ],
  GET_PRIVATE_ENDPOINT_LIST: (organizationId: string, projectId: string, databaseId: string) => [
    'getPrivateEndpointList',
    organizationId,
    projectId,
    databaseId,
  ],
  GET_ACCESSIBLE_DATABASES: (organizationId: string) => ['getAccessibleDatabases', organizationId],
  MODEL_LIST: (organizationId: string, clusterId?: string) => ['modelList', organizationId, clusterId],
  GET_MODEL: (organizationId: string, languageModelId: string) => ['model', organizationId, languageModelId],
  MODELS_LIST: (organizationId: string, type?: ModelType) => ['modelsList', organizationId, type],
  MODEL_DEPLOYMENT_OPTIONS_LIST: (organizationId: string, paginationDetails: PaginationParams) => [
    'modelDeploymentOptionsList',
    organizationId,
    paginationDetails,
  ],
  MODEL_DEPLOYMENT_REGIONS: (organizationId: string, provider: string) => ['modelDeploymentRegions', organizationId, provider],
  MODEL_DEPLOYMENT_COMPUTE: (organizationId: string, provider: string, region: string, modelcatalogId: string) => [
    'modelDeploymentCompute',
    organizationId,
    provider,
    region,
    modelcatalogId,
  ],
  MODEL_API_KEY_DETAILS: (organizationId: string, languageModelId: string, apiKeyId: string) => [
    'modelApiKeyDetails',
    organizationId,
    languageModelId,
    apiKeyId,
  ],
  MODEL_API_KEYS_LIST: (organizationId: string, languageModelId: string) => ['modelApiKeys', organizationId, languageModelId],
  INTEGRATIONS_COUNT: (orgId: string) => ['integrationsCount', orgId],
  INTEGRATIONS_LIST: (orgId: string, page: PaginationParams, integrationType?: IntegrationTypeParam) => [
    'integrationsList',
    orgId,
    page,
    integrationType,
  ],
  SERVICES_LIST: (organizationId: string, projectId: string, databaseId: string) => ['servicesList', organizationId, projectId, databaseId],
  ADDITIONAL_HEALTH_INFO: (organizationId: string, projectId: string, databaseId: string, reportId: string, category: string) => [
    'additionalHealthInfo',
    organizationId,
    projectId,
    databaseId,
    reportId,
    category,
  ],
  GET_LM_SERVICE_ELIGIBLE_CLUSTERS: (organizationId: string) => ['getLmServiceEligibleClusters', organizationId],
  GET_VPC_LIST: (organizationId: string, projectId: string, databaseId: string) => ['getVPCList', organizationId, projectId, databaseId],
  PLAYGROUND_EXAMPLES: (organizationId: string) => ['playgroundExamples', organizationId],
  PLAYGROUND_EXAMPLES_METADATA: (organizationId: string) => ['playgroundExamplesMetaData', organizationId],
  PLAYGROUND_EXAMPLES_METADATA_BY_TYPE: (organizationId: string) => ['playgroundExamplesMetaDataByType', organizationId],
  GET_VPC: (organizationId: string, projectId: string, databaseId: string, vpcId: string) => [
    'getVpc',
    organizationId,
    projectId,
    databaseId,
    vpcId,
  ],
  PROJECT_LEVEL_BACKUPS: (organizationId: string, projectId: string) => ['projectLevelBackups', organizationId, projectId],
  TARGET_CLUSTERS: (organizationId: string, projectId: string, backupId: string) => ['targetClusters', organizationId, projectId, backupId],
  GET_DATABASE_MAINTENANCE_JOB: (maintenanceId: string, organizationId: string, databaseId: string, projectId: string) => [
    'getDatabaseMaintenanceJob',
    maintenanceId,
    databaseId,
    organizationId,
    projectId,
  ],
  GET_PAYMENT_METHOD_LIST: (organizationId: string) => ['getPaymentMethodList', organizationId],
  GET_RESOURCES_LIST: (organizationId: string) => ['getResourcesList', organizationId],
  GET_CUSTOMER_ID: (organizationId: string) => ['getCustomerId', organizationId],
  GET_ACTIVATION_RESTRICTION: (organizationId: string) => ['getActivationAndRestriction', organizationId],
  GET_CARD_EXPIRY_INFO: (organizationId: string) => ['getCardExpiryInfo', organizationId],
  AVAILABLE_REGIONS: (organizationId: string, projectId: string, databaseId: string) => [
    'availableRegions',
    organizationId,
    projectId,
    databaseId,
  ],
  NAME_CHECKER: (organizationId: string, resourceType: NameCheckerResourceType) => ['nameChecker', organizationId, resourceType],
} as const;

const PERSIST_KEYS_WHITELIST = ['organizationList', 'trial', 'projectList'];

const persister = createSyncStoragePersister({
  storage: window.localStorage,
});

export const persistOptions: PersistQueryClientProviderProps['persistOptions'] = {
  persister,
  dehydrateOptions: {
    shouldDehydrateQuery: ({ queryKey }) => {
      if (!ALLOW_LOCAL_STORAGE) {
        return false;
      }

      return PERSIST_KEYS_WHITELIST.includes(String(queryKey[0]));
    },
  },
};
